import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, ShieldCheck, Instagram, Menu, Phone, X } from "lucide-react";
import { LOGO, MARK, FLEET_IMG as FLEET, Reveal, MaskedHeading, gbp } from "@/components/common";
import { VEHICLES, CONTACT_NUMBER, WHATSAPP_URL, waLink } from "@/data/vehicles";

const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

/* ---------------- NAV ---------------- */
const Nav = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const goTo = (fn) => { setMenuOpen(false); fn(); };
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#050506]/85 backdrop-blur-md shadow-[inset_0_-1px_0_rgba(255,255,255,0.08)]" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo-button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3"
        >
          <img src={MARK} alt="Senna Prestige Travels" className="h-9 w-auto" />
          <span className="hidden sm:block text-[0.6rem] tracking-[0.42em] uppercase text-zinc-300 font-light">
            Senna Prestige Travels
          </span>
        </button>
        <nav className="hidden md:flex items-center gap-10 text-[0.62rem] tracking-[0.3em] uppercase text-zinc-400" data-testid="desktop-nav">
          <button data-testid="nav-services-link" className="nav-link" onClick={() => scrollTo("services")}>Services</button>
          <button data-testid="nav-fleet-link" className="nav-link" onClick={() => scrollTo("fleet")}>Fleet</button>
          <button data-testid="nav-booking-link" className="nav-link" onClick={() => scrollTo("booking")}>Booking</button>
          <button data-testid="nav-why-link" className="nav-link" onClick={() => scrollTo("why")}>Why Senna</button>
          <button data-testid="nav-recovery-link" className="nav-link" onClick={() => navigate("/recovery")}>Senna Recovery</button>
        </nav>
        <div className="flex items-center gap-4">
          <button data-testid="nav-book-button" className="btn-ghost !py-3 !px-5 !text-[0.6rem]" onClick={() => navigate("/booking")}>
            Book Your Journey
          </button>
          <button
            data-testid="nav-menu-button"
            className="md:hidden text-zinc-200 p-2 -mr-2"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            {menuOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div data-testid="mobile-menu" className="md:hidden bg-[#050506]/97 backdrop-blur-md border-t border-b border-white/[0.08]">
          <div className="px-6 py-6 flex flex-col">
            {[
              ["Services", () => goTo(() => scrollTo("services"))],
              ["Fleet", () => goTo(() => scrollTo("fleet"))],
              ["Booking", () => goTo(() => scrollTo("booking"))],
              ["Why Senna", () => goTo(() => scrollTo("why"))],
              ["Senna Recovery", () => goTo(() => navigate("/recovery"))],
            ].map(([label, fn]) => (
              <button
                key={label}
                data-testid={`mobile-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={fn}
                className="text-left py-4 text-[0.68rem] tracking-[0.35em] uppercase text-zinc-300 border-b border-white/[0.05] last:border-0"
              >
                {label}
              </button>
            ))}
            <button
              data-testid="mobile-nav-book"
              onClick={() => goTo(() => navigate("/booking"))}
              className="btn-primary justify-center mt-5"
            >
              Book Your Journey <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

/* ---------------- HERO ---------------- */
const Hero = () => {
  const navigate = useNavigate();
  return (
  <section data-testid="hero-section" className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden px-6">
    <div className="hero-glow" />
    <div className="hero-floor" />

    <motion.img
      src={LOGO}
      alt="Senna Prestige Travels — Hire, Chauffeur, Transport"
      data-testid="hero-logo"
      className="w-[240px] sm:w-[320px] md:w-[380px] h-auto mb-14"
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
    />

    <motion.p
      data-testid="hero-kicker"
      className="kicker mb-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1, delay: 0.5 }}
    >
      Prestige Travel, Redefined.
    </motion.p>

    <motion.h1
      data-testid="hero-heading"
      className="font-display text-4xl sm:text-5xl lg:text-6xl text-center leading-[1.12] text-white max-w-3xl overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.5 }}
    >
      <motion.span
        className="block pb-2"
        initial={{ y: "108%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        Your Journey, Upgraded.
      </motion.span>
    </motion.h1>

    <motion.p
      data-testid="hero-subline"
      className="mt-7 text-sm md:text-base text-zinc-400 font-light tracking-[0.14em] flex flex-wrap items-center justify-center gap-x-4 gap-y-2"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1, delay: 0.9 }}
    >
      <span>Premium Vehicle Hire</span>
      <span className="hidden sm:inline text-zinc-600">•</span>
      <span>Chauffeur</span>
      <span className="hidden sm:inline text-zinc-600">•</span>
      <span>Transport</span>
    </motion.p>

    <motion.div
      className="mt-12 flex flex-col sm:flex-row items-center gap-5"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, delay: 1.1 }}
    >
      <button data-testid="hero-explore-fleet-button" className="btn-primary" onClick={() => scrollTo("fleet")}>
        Explore The Fleet <ArrowRight size={14} />
      </button>
      <button data-testid="hero-book-journey-button" className="btn-ghost" onClick={() => navigate("/booking")}>
        Book Your Journey
      </button>
    </motion.div>

    <motion.button
      data-testid="hero-scroll-cue"
      onClick={() => scrollTo("services")}
      className="absolute bottom-10 scroll-cue text-zinc-500"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.8, duration: 1 }}
      aria-label="Scroll down"
    >
      <ChevronDown size={22} strokeWidth={1.25} />
    </motion.button>
  </section>
  );
};

/* ---------------- MARQUEE ---------------- */
const Marquee = () => {
  const items = ["Prestige Vehicle Hire", "Professional Chauffeur", "Tailored Transport", "Delivery & Collection", "Senna Prestige Travels"];
  const row = [...items, ...items];
  return (
    <div data-testid="marquee-strip" className="py-8 overflow-hidden border-y border-white/[0.07]">
      <div className="marquee-track">
        {[0, 1].map((half) => (
          <div key={half} className="flex items-center shrink-0">
            {row.map((t, i) => (
              <span key={`${half}-${i}`} className="flex items-center text-[0.62rem] tracking-[0.45em] uppercase text-zinc-600 whitespace-nowrap">
                <span className="px-8">{t}</span>
                <span className="w-1 h-1 rounded-full bg-zinc-700" />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------------- SERVICES ---------------- */
const services = [
  { n: "01", title: "Vehicle Hire", desc: "Premium vehicles available for short and longer-term hire." },
];

const Services = () => (
  <section id="services" data-testid="services-section" className="max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-40">
    <Reveal>
      <p className="kicker mb-5">Prestige Services</p>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white max-w-xl leading-[1.15]">
        <MaskedHeading>Premium vehicle hire, tailored around your journey.</MaskedHeading>
      </h2>
    </Reveal>

    <div className="mt-20 grid grid-cols-1 max-w-xl border-t border-white/[0.08]">
      {services.map((s, i) => (
        <Reveal key={s.n} delay={i * 0.12} className={`service-item px-2 md:px-10 py-12 md:py-16 border-b md:border-b-0 border-white/[0.08] ${i > 0 ? "md:border-l" : ""} ${i === 0 ? "md:pl-0" : ""}`}>
          <span className="service-number font-display text-lg text-zinc-600">{s.n}</span>
          <h3 className="mt-8 text-[0.72rem] tracking-[0.4em] uppercase text-white">{s.title}</h3>
          <div className="mt-5 h-px w-10 bg-white/25" />
          <p className="mt-6 text-sm text-zinc-400 font-light leading-relaxed">{s.desc}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------- FLEET ---------------- */
const Fleet = () => {
  const frameRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: frameRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-7%", "7%"]);

  return (
  <section id="fleet" data-testid="fleet-section" className="max-w-6xl mx-auto px-6 md:px-10 py-28 md:py-36">
    <Reveal className="text-center">
      <p className="kicker mb-5">Our Fleet</p>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.15]">
        <MaskedHeading>A Fleet Built For Every Journey</MaskedHeading>
      </h2>
    </Reveal>

    <Reveal delay={0.15} className="mt-16">
      <div className="fleet-frame overflow-hidden" data-testid="fleet-photo-frame" ref={frameRef}>
        <motion.div
          initial={{ clipPath: "inset(7% 4% 7% 4%)", opacity: 0.65 }}
          whileInView={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.img
            src={FLEET}
            alt="The Senna Prestige Travels vehicle fleet"
            style={{ y: imgY, scale: 1.14 }}
          />
        </motion.div>
        <div className="fleet-shade" />
      </div>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
        {["Performance", "Luxury", "Executive", "Prestige"].map((label) => (
          <span key={label} data-testid={`fleet-label-${label.toLowerCase()}`} className="text-[0.62rem] tracking-[0.42em] uppercase text-zinc-500">
            {label}
          </span>
        ))}
      </div>
      <div className="mt-12 text-center">
        <button data-testid="fleet-view-vehicles-link" className="link-arrow" onClick={() => scrollTo("fleet-list")}>
          View Available Vehicles <ArrowRight size={14} />
        </button>
      </div>
    </Reveal>

    <FleetList />
  </section>
  );
};

/* ---------------- FLEET LIST ---------------- */
const FleetList = () => {
  const navigate = useNavigate();
  return (
    <Reveal className="mt-24" delay={0.1}>
      <div id="fleet-list" className="scroll-mt-28">
        <p className="kicker mb-8 text-center">Current Fleet & Hire Rates</p>
        <div className="hidden md:grid grid-cols-[1.6fr_repeat(5,1fr)] gap-4 pb-5 border-b border-white/[0.14]">
          {["Vehicle", "Daily", "Weekend", "Week", "Month", "Deposit"].map((h) => (
            <span key={h} className="text-[0.58rem] tracking-[0.32em] uppercase text-zinc-500">{h}</span>
          ))}
        </div>
        {VEHICLES.map((v) => (
          <button
            key={v.id}
            data-testid={`fleet-row-${v.id}`}
            onClick={() => navigate(`/booking?vehicle=${v.id}`)}
            className="fleet-row group block w-full text-left"
          >
            <div className="hidden md:grid grid-cols-[1.6fr_repeat(5,1fr)] gap-4 items-center py-6 border-b border-white/[0.07]">
              <span className="font-display text-xl text-white flex items-center gap-4">
                {v.photos?.length > 0 && (
                  <img src={v.photos[0]} alt={v.name} className="w-20 h-[52px] object-cover shrink-0" loading="lazy" />
                )}
                {v.name}
                <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-zinc-400" />
              </span>
              {v.enquiryOnly ? (
                <>
                  <span className="col-span-4 text-[0.62rem] tracking-[0.3em] uppercase text-zinc-400">Enquire for Price</span>
                  <a
                    data-testid={`fleet-enquire-${v.id}`}
                    href={waLink(`Hello Senna Prestige Travels — I would like to enquire about hiring the ${v.name}.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-[0.6rem] tracking-[0.25em] uppercase text-white underline underline-offset-4 hover:text-zinc-300"
                  >
                    WhatsApp
                  </a>
                </>
              ) : (
                <>
                  <span className="text-sm text-zinc-300 font-light">{gbp(v.prices.daily)}</span>
                  <span className="text-sm text-zinc-300 font-light">{gbp(v.prices.weekend)}</span>
                  <span className="text-sm text-zinc-300 font-light">{gbp(v.prices.week)}</span>
                  <span className="text-sm text-zinc-300 font-light">{gbp(v.prices.month)}</span>
                  <span className="text-sm text-zinc-500 font-light">{gbp(v.deposit)}</span>
                </>
              )}
            </div>
            <div className="md:hidden py-6 border-b border-white/[0.07]">
              <div className="flex items-center justify-between gap-4">
                <span className="font-display text-lg text-white flex items-center gap-3">
                  {v.photos?.length > 0 && (
                    <img src={v.photos[0]} alt={v.name} className="w-16 h-11 object-cover shrink-0" loading="lazy" />
                  )}
                  {v.name}
                </span>
                {!v.enquiryOnly && (
                  <span className="text-[0.58rem] tracking-[0.22em] uppercase text-zinc-500 whitespace-nowrap">From {gbp(v.prices.daily)} / day</span>
                )}
              </div>
              {v.enquiryOnly ? (
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-[0.62rem] tracking-[0.3em] uppercase text-zinc-400">Enquire for Price</span>
                  <a
                    data-testid={`fleet-enquire-m-${v.id}`}
                    href={waLink(`Hello Senna Prestige Travels — I would like to enquire about hiring the ${v.name}.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-[0.6rem] tracking-[0.25em] uppercase text-white underline underline-offset-4"
                  >
                    WhatsApp
                  </a>
                </div>
              ) : (
                <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-zinc-400 font-light">
                  <span>Weekend — {gbp(v.prices.weekend)}</span>
                  <span>Week — {gbp(v.prices.week)}</span>
                  <span>Month — {gbp(v.prices.month)}</span>
                  <span>Deposit — {gbp(v.deposit)}</span>
                </div>
              )}
            </div>
          </button>
        ))}
        <p className="mt-7 text-[0.6rem] tracking-[0.22em] uppercase text-zinc-600 leading-relaxed">
          Weekend hire runs Friday morning to Monday morning. Security deposits are refundable. Select any vehicle to begin your booking. Vehicles marked Enquire for Price are arranged directly via WhatsApp on +44 7457 418474.
        </p>
      </div>
    </Reveal>
  );
};

/* ---------------- HIRE INFORMATION ---------------- */
const hireInfo = [
  { title: "Driver Requirements", items: ["Minimum driver age 21", "Driving licence — front and back", "Bank statement or utility bill, no older than 6 months", "National Insurance number (optional)"] },
  { title: "Hire Packages", items: ["Daily hire", "Weekend — Friday morning to Monday morning", "Week hire", "Month hire"] },
  { title: "Delivery & Collection", items: ["Based in Peterborough", "Nationwide delivery available", "£2.20 per mile", "Delivery and collection are two separate journeys"] },
  { title: "Booking Approval", items: ["Every booking reviewed by our team", "Payment authorised at submission", "Payment captured only on approval", "Declined bookings released in full"] },
];

const HireInfo = () => (
  <section id="hire-info" data-testid="hire-info-section" className="max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-36">
    <Reveal>
      <p className="kicker mb-5">Before You Book</p>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white max-w-xl leading-[1.15]">
        <MaskedHeading>Everything made clear, before you commit.</MaskedHeading>
      </h2>
    </Reveal>
    <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-white/[0.08]">
      {hireInfo.map((col, i) => (
        <Reveal key={col.title} delay={i * 0.1} className={`service-item px-2 lg:px-8 py-12 border-b lg:border-b-0 border-white/[0.08] ${i > 0 ? "lg:border-l" : ""} ${i === 0 ? "lg:pl-0" : ""}`}>
          <h3 className="text-[0.68rem] tracking-[0.35em] uppercase text-white">{col.title}</h3>
          <div className="mt-5 h-px w-10 bg-white/25" />
          <ul className="mt-6 space-y-3">
            {col.items.map((item) => (
              <li key={item} className="text-sm text-zinc-400 font-light leading-relaxed flex gap-3">
                <span className="mt-[0.55rem] w-1 h-1 rounded-full bg-zinc-600 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------- BOOKING PREVIEW ---------------- */
const BOOKING_STEPS = [
  "Select Vehicle", "Select Hire Package", "Select Dates", "Collection / Delivery",
  "Customer Details", "Driver Verification", "Rental Agreement", "Payment Summary", "Submit for Approval",
];

const BookingPreview = () => {
  const navigate = useNavigate();
  return (
    <section id="booking" data-testid="booking-section" className="border-y border-white/[0.07] bg-[#08080a]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-40">
        <Reveal className="max-w-2xl">
          <p className="kicker mb-5">The Booking Experience</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.15]">
            <MaskedHeading>A complete online booking journey, designed for prestige.</MaskedHeading>
          </h2>
          <p className="mt-6 text-sm md:text-base text-zinc-400 font-light leading-relaxed">
            Nine refined steps from choosing your vehicle to secure submission — with online payment authorisation, driver document verification and a digitally accepted rental agreement. Every booking is reviewed and approved by our team before payment is captured.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {["Secure Booking", "Document Verification", "Online Agreements"].map((t) => (
              <span key={t} data-testid={`status-pill-${t.toLowerCase().replace(/\s/g, "-")}`} className="flex items-center gap-2 px-4 py-2 text-[0.58rem] tracking-[0.28em] uppercase text-zinc-300 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)]">
                <ShieldCheck size={12} className="text-zinc-500" /> {t}
              </span>
            ))}
          </div>
          <button data-testid="booking-start-button" className="btn-primary mt-10" onClick={() => navigate("/booking")}>
            Begin Your Booking <ArrowRight size={14} />
          </button>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.08] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]">
          {BOOKING_STEPS.map((s, i) => (
            <Reveal key={s} delay={i * 0.05}>
              <div data-testid={`booking-step-preview-${i + 1}`} className="step-preview-cell bg-[#08080a] px-7 py-8 h-full">
                <span className="font-display text-zinc-600">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-4 text-[0.68rem] tracking-[0.28em] uppercase text-zinc-200">{s}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- WHY ---------------- */
const whys = [
  { title: "Premium Vehicles", desc: "Carefully selected prestige and performance vehicles." },
  { title: "Flexible Hire", desc: "Short and longer-term hire options." },
  { title: "Professional Service", desc: "A premium experience from enquiry through to collection." },
  { title: "Door-to-Door Options", desc: "Delivery and collection options available." },
];

const Why = () => (
  <section id="why" data-testid="why-section" className="max-w-6xl mx-auto px-6 md:px-10 py-28 md:py-40">
    <Reveal>
      <p className="kicker mb-5">Why Senna Prestige</p>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white max-w-lg leading-[1.15]">
        <MaskedHeading>The details define the journey.</MaskedHeading>
      </h2>
    </Reveal>
    <div className="mt-16 border-t border-white/[0.08]">
      {whys.map((w, i) => (
        <Reveal key={w.title} delay={i * 0.08}>
          <div data-testid={`why-row-${i + 1}`} className="why-row grid grid-cols-1 md:grid-cols-[1fr_1.4fr] gap-2 md:gap-10 py-9 border-b border-white/[0.08]">
            <h3 className="text-[0.7rem] tracking-[0.38em] uppercase text-white pt-1">{w.title}</h3>
            <p className="text-sm md:text-base text-zinc-400 font-light">{w.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------- FINAL CTA ---------------- */
const FinalCTA = () => {
  const navigate = useNavigate();
  return (
  <section data-testid="final-cta-section" className="relative border-t border-white/[0.07] overflow-hidden">
    <div className="hero-glow" style={{ top: "-55%" }} />
    <div className="relative max-w-4xl mx-auto px-6 py-32 md:py-44 text-center">
      <Reveal>
        <img src={LOGO} alt="Senna Prestige Travels" className="w-[200px] sm:w-[260px] h-auto mx-auto mb-14" />
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.15]">
          <MaskedHeading>Your Next Journey Starts Here.</MaskedHeading>
        </h2>
        <p className="mt-7 text-sm md:text-base text-zinc-400 font-light leading-relaxed max-w-xl mx-auto">
          From prestige vehicle hire to professional chauffeur travel, experience Senna Prestige Travels.
        </p>
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-5">
          <button data-testid="final-book-journey-button" className="btn-primary" onClick={() => navigate("/booking")}>
            Book Your Journey <ArrowRight size={14} />
          </button>
          <a data-testid="final-contact-button" className="btn-ghost" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
            Contact Senna Prestige
          </a>
        </div>
        <div className="mt-14 flex items-center justify-center gap-8">
          <span data-testid="social-instagram" className="social-icon" title="Instagram"><Instagram size={18} strokeWidth={1.5} /></span>
          <span data-testid="social-tiktok" className="social-icon" title="TikTok"><TikTokIcon size={17} /></span>
        </div>
        <a
          data-testid="contact-phone-link"
          href="tel:+447457418474"
          className="mt-10 inline-flex items-center gap-3 text-sm tracking-[0.22em] text-zinc-400 hover:text-white transition-colors duration-300"
        >
          <Phone size={14} strokeWidth={1.5} /> {CONTACT_NUMBER}
        </a>
      </Reveal>
    </div>
  </section>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="border-t border-white/[0.07]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 flex flex-col sm:flex-row flex-wrap items-center justify-between gap-4">
      <span className="text-[0.58rem] tracking-[0.35em] uppercase text-zinc-600">Senna Prestige Travels</span>
      <a href="tel:+447457418474" data-testid="footer-phone-link" className="text-[0.58rem] tracking-[0.3em] uppercase text-zinc-500 hover:text-white transition-colors">{CONTACT_NUMBER}</a>
      <span className="text-[0.58rem] tracking-[0.3em] uppercase text-zinc-700">Hire — Chauffeur — Transport</span>
      <a href="/admin" data-testid="footer-admin-link" className="text-[0.58rem] tracking-[0.3em] uppercase text-zinc-600 hover:text-white transition-colors">Admin Preview</a>
    </div>
  </footer>
);

export default function Home() {
  return (
    <div className="bg-[#050506] text-zinc-100 min-h-screen">
      <div className="vignette-overlay" />
      <Nav />
      <Hero />
      <Marquee />
      <Services />
      <Fleet />
      <HireInfo />
      <BookingPreview />
      <Why />
      <FinalCTA />
      <Footer />
    </div>
  );
}
